import { useSearchParams } from 'next/navigation';
import { useContext, useEffect, useState } from 'react';
import { PageContext } from 'src/context/PageContext';
import { LogError } from './loggingHelpers';

export interface utmFormat {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  utm_id: string | null;
  referrer: string | null;
}

export const useUTM = (mockSearchParams?: Map<string, string>) => {
  const [utmCodes, setUtmCodes] = useState<utmFormat | null>(null);
  const { theme } = useContext(PageContext);
  const searchParamsHook = useSearchParams();
  const searchParams = mockSearchParams || searchParamsHook; // allow optional mock to be passed in for vitest
  useEffect(() => {
    try {
      // capture and save utm codes to session storage.
      if (!window?.sessionStorage) {
        return;
      }

      // also save referrer if exists
      const pageReferrer = document.referrer;
      const legacySiteReferrer =
        pageReferrer.includes(`${theme?.organization}.civilspace.io`) ||
        pageReferrer.includes(`${theme?.organization}.civilspace.dev`);
      const pageReferrerExists =
        !!pageReferrer && !pageReferrer.includes(window?.location?.origin) && !legacySiteReferrer;
      let referrerHostname = '';
      if (pageReferrerExists) {
        window.sessionStorage.setItem('referrer', pageReferrer);
        referrerHostname = new URL(pageReferrer).hostname.replace('www.', '');
      }

      const utmTypes = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'];
      utmTypes.forEach((utmType) => {
        const stringUtm = searchParams.get(utmType);
        if (stringUtm) {
          window.sessionStorage.setItem(utmType, stringUtm);
        } else if (utmType === 'utm_medium' && pageReferrerExists) {
          // special case if utm_medium is blank but a referrer exists
          const socialReferrers = [
            'facebook.com',
            'instagram.com',
            'x.com',
            'whatsapp.com',
            'reddit.com',
            'tiktok.com',
            'linkedin.com',
            'vk.com',
            'pinterest.com',
            'twitter.com',
            'discord.com',
            'ok.ru',
            'telegram.org',
            'namu.wiki',
            'messenger.com',
            'ameblo.jp',
            'zhihu.com',
            'line.me',
            'nextdoor.com',
            'weibo.com',
            'tumblr.com',
            'kwai.com',
            'heylink.me',
            'slack.com',
            'ssstik.io',
            'efh8z.com',
            'snapchat.comthreads.net',
            'bakusai.com',
            'patreon.com',
            'zalo.me',
            'xiaohongshu.com',
            'snaptik.app',
            'slideshare.net',
            'quark.cn',
            'hatenablog.com',
            'discordapp.com',
            'ptt.cc',
            'fastdl.app',
            'atid.me',
            'livejournal.com',
            'pinterest.co.uk',
            'condles-temark.com',
            'feishu.cn',
            'pinterest.fr',
            'dcard.tw',
            'pinterest.de',
          ];
          const isSubDomain = () => {
            let isSubDomainBoolean = false;
            socialReferrers.forEach((socialReferrer) => {
              if (referrerHostname.endsWith(`.${socialReferrer}`)) {
                isSubDomainBoolean = true;
              }
            });
            return isSubDomainBoolean;
          };
          if (socialReferrers.includes(referrerHostname) || isSubDomain()) {
            window.sessionStorage.setItem('utm_medium', 'social');
          } else {
            window.sessionStorage.setItem('utm_medium', 'referral');
          }
        } else if (utmType === 'utm_source' && pageReferrerExists) {
          const getDomain = () => {
            if (referrerHostname.split('.').length > 2) {
              return referrerHostname.split('.').slice(-2).join('.');
            }
            return referrerHostname;
          };
          // special case if source is blank but referrer exists
          window.sessionStorage.setItem('utm_source', getDomain());
        }
      });

      const returnObject = {
        utm_source: null,
        utm_medium: null,
        utm_campaign: null,
        utm_term: null,
        utm_content: null,
        utm_id: null,
        referrer: null,
      } as utmFormat;
      if (window?.sessionStorage) {
        returnObject.utm_source = window.sessionStorage.getItem('utm_source');
        returnObject.utm_medium = window.sessionStorage.getItem('utm_medium');
        returnObject.utm_campaign = window.sessionStorage.getItem('utm_campaign');
        returnObject.utm_term = window.sessionStorage.getItem('utm_term');
        returnObject.utm_content = window.sessionStorage.getItem('utm_content');
        returnObject.utm_id = window.sessionStorage.getItem('utm_id');
        returnObject.referrer = window.sessionStorage.getItem('referrer');
      }
      setUtmCodes(returnObject);
    } catch (error) {
      // log possible error with browser blocking session storage
      LogError('Error in useUTM', error);
      setUtmCodes({
        utm_source: null,
        utm_medium: null,
        utm_campaign: null,
        utm_term: null,
        utm_content: null,
        utm_id: null,
        referrer: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return utmCodes;
};
